import { MarkNode, type SerializedMarkNode } from '@lexical/mark';
import { NodeKey, LexicalNode } from 'lexical';

export interface CommentData {
  id: string;
  content: string;
  author: string;
  timestamp: number;
  resolved: boolean;
  replies?: CommentReply[];
  groupId?: string; // Groups split nodes that belong to the same logical comment
}

export interface CommentReply {
  id: string;
  content: string;
  author: string;
  timestamp: number;
}

export interface SerializedCommentNode extends SerializedMarkNode {
  commentData: CommentData;
}

export class CommentNode extends MarkNode {
  __commentData: CommentData;

  static getType(): string {
    return 'comment';
  }

  static clone(node: CommentNode): CommentNode {
    return new CommentNode(node.__commentData, [...node.__ids], node.__key);
  }

  constructor(commentData: CommentData, ids?: Array<string>, key?: NodeKey) {
    super(ids || [], key);
    this.__commentData = commentData;
  }

  getCommentData(): CommentData {
    return this.__commentData;
  }

  setCommentData(commentData: CommentData): void {
    const writable = this.getWritable();
    writable.__commentData = commentData;
  }

  createDOM(): HTMLElement {
    const element = document.createElement('mark');
    const hasMultipleIds = this.__ids.length > 1;

    if (this.__commentData.resolved) {
      element.className = 'comment-mark-resolved';
      element.style.display = 'contents';
    } else if (hasMultipleIds) {
      // Apply overlap styling when multiple comment IDs are present
      element.className = 'comment-mark editor-mark-overlap';
    } else {
      element.className = 'comment-mark';
    }

    element.setAttribute('data-comment-id', this.__commentData.id);
    element.setAttribute('data-resolved', this.__commentData.resolved.toString());

    // Store all comment IDs for overlap handling
    if (hasMultipleIds) {
      element.setAttribute('data-comment-ids', this.__ids.join(','));
      element.setAttribute('data-overlap', 'true');
    }

    return element;
  }

  updateDOM(prevNode: this, dom: HTMLElement): boolean {
    const prevCommentData = prevNode.__commentData;
    const currentCommentData = this.__commentData;
    const prevHasMultipleIds = prevNode.__ids.length > 1;
    const currentHasMultipleIds = this.__ids.length > 1;

    // Update if resolution status or overlap status changed
    if (prevCommentData.resolved !== currentCommentData.resolved ||
      prevHasMultipleIds !== currentHasMultipleIds) {

      dom.setAttribute('data-resolved', currentCommentData.resolved.toString());

      if (currentCommentData.resolved) {
        dom.className = 'comment-mark-resolved';
        dom.style.display = 'contents';
        dom.removeAttribute('data-overlap');
      } else if (currentHasMultipleIds) {
        dom.className = 'comment-mark editor-mark-overlap';
        dom.style.display = '';
        dom.setAttribute('data-comment-ids', this.__ids.join(','));
        dom.setAttribute('data-overlap', 'true');
      } else {
        dom.className = 'comment-mark';
        dom.style.display = '';
        dom.removeAttribute('data-overlap');
        dom.removeAttribute('data-comment-ids');
      }

      return false;
    }

    return false;
  }

  static importDOM(): null {
    return null;
  }

  static importJSON(serializedNode: SerializedCommentNode): CommentNode {
    const { commentData, ids } = serializedNode;
    return new CommentNode(commentData, ids);
  }

  exportJSON(): SerializedCommentNode {
    return {
      ...super.exportJSON(),
      commentData: this.__commentData,
      type: 'comment',
      version: 1,
    };
  }

  canInsertTextBefore(): false {
    return false;
  }

  canInsertTextAfter(): false {
    return false;
  }

  isInline(): true {
    return true;
  }

  /** Check if this comment node contains a specific comment ID (handles both single and overlapping comments).  */
  hasCommentId(commentId: string): boolean {
    return this.__ids.includes(commentId);
  }

  /** Get all comment IDs associated with this node. */
  getAllCommentIds(): string[] {
    return [...this.__ids];
  }
}


export function $createCommentNode(commentData: CommentData, ids?: Array<string>): CommentNode {
  return new CommentNode(commentData, ids);
}

export function $isCommentNode(node: LexicalNode | null | undefined): node is CommentNode {
  return node instanceof CommentNode;
}